import { RiBardFill } from "@remixicon/react";
import Markdown from "react-markdown";
import useEvaluator from "~/hooks/use-evaluator";
import EvaluatorStatus from "./evaluator-status";
import LabeledComponent from "./labeled-component";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import type { CreateMessage, Message } from "ai";
import { cn } from "~/lib/utils";
import useKeyboardShortcut from "~/hooks/use-keyboard-shortcut";

interface IProps {
  appendVacancyMessage: (message: Message | CreateMessage) => void;
  disabled?: boolean;
}

const InputEvaluator = ({ appendVacancyMessage, disabled }: IProps) => {
  const {
    chat: { handleInputChange, handleSubmit, input },
    evaluation,
    isEvaluating,
    feedback,
    percentage,
  } = useEvaluator();

  useKeyboardShortcut({
    func: handleSubmit,
    keyboardKey: "Enter",
    modifierKey: true,
  });

  return (
    <>
      <form
        className="flex flex-col gap-2 items-end w-full"
        onSubmit={handleSubmit}
      >
        <LabeledComponent label="Beschrijf je vacature" className="w-full">
          <EvaluatorStatus status={isEvaluating ? "PENDING" : evaluation} />
          <div>
            <Textarea
              disabled={isEvaluating}
              value={input}
              name="description"
              className="h-48"
              id="description"
              placeholder={
                isEvaluating
                  ? "Aan het evalueren..."
                  : "Plak een e-mail, gespreksnotities of begin zelf met typen..."
              }
              onChange={handleInputChange}
            />
          </div>
          {percentage !== null && !isEvaluating && evaluation && (
            <div className="h-5 w-full rounded-full overflow-hidden bg-muted">
              <div
                className={cn(
                  "h-full flex justify-end pr-1.5 items-center rounded-r-full min-w-7",
                  percentage > 70 ? "bg-green-500/30" : "bg-yellow-400/30"
                )}
                style={{ width: `${percentage}%` }}
              >
                <p
                  className={cn(
                    "text-xs",
                    percentage > 70 ? "text-green-500" : "text-yellow-500"
                  )}
                >
                  {percentage}%
                </p>
              </div>
            </div>
          )}
        </LabeledComponent>
        {feedback && (
          <div className="bg-secondary w-full p-4 rounded-md">
            <Markdown className="prose prose-p:m-0">{feedback}</Markdown>
          </div>
        )}
        <div className="flex gap-2">
          <Button
            variant={evaluation === "PASS" ? "outline" : "default"}
            size={"withIcon"}
            type="submit"
            disabled={isEvaluating || disabled}
          >
            <RiBardFill />
            Evalueer beschrijving
          </Button>
          <Button
            type="button"
            variant={evaluation === "PASS" ? "default" : "outline"}
            size={"withIcon"}
            onClick={() =>
              appendVacancyMessage({ role: "user", content: input })
            }
            disabled={isEvaluating || disabled}
          >
            <RiBardFill />
            Genereren
          </Button>
        </div>
      </form>
    </>
  );
};

export default InputEvaluator;
